// src/components/settings/EmailSettingsTab.tsx
import React from "react";
import {
  Card,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Divider,
  Stack,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Save as SaveIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import { EmailConfig } from "../../services/emailService";
import {
  ValidationErrors,
  validatePhone,
  formatPhoneNumber,
} from "./ProfileTab";
import { runtimeEnvironment } from "../../lib/runtimeEnvironment";
import { ThemeProvider } from "@mui/material/styles";
import { buildTheme } from "../../theme/theme";

// Constants
const COMPANY_NAME = "LocateNow, LLC";
const COMPANY_LOGO_URL = `${runtimeEnvironment.assetBaseUrl}/locatenow-logo.png`;

// Email validation helper
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

interface EmailSettingsTabProps {
  emailConfig: EmailConfig;
  setEmailConfig: React.Dispatch<React.SetStateAction<EmailConfig>>;
  validationErrors: ValidationErrors;
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  onSave: () => void;
}


/**
 * The signature preview, as its OWN component — and that is the load-bearing
 * part, not a tidiness choice.
 *
 * The preview is a frozen-light surface: it shows what the RECIPIENT sees, and
 * email clients render on white. Freezing the background without freezing the
 * foreground is what made it invisible in dark mode (1.10:1).
 *
 * The first fix wrapped the JSX in a <ThemeProvider> — which fixed string
 * tokens (`color="text.secondary"`) but NOT `theme.palette.*`, because the
 * parent's `useTheme()` still held the APP theme in scope for that JSX. A
 * reviewer proved it by adding one child: `sx={{ color: theme.palette.text.primary }}`
 * reintroduced the original bug verbatim, at 1.10:1.
 *
 * Living in its own component, `useTheme()` below resolves to the PREVIEW
 * theme. The app theme is not in scope at all, so the mistake is unavailable
 * rather than merely discouraged — which is what "a child added later inherits
 * the fix" has to mean to be worth claiming.
 */
function SignaturePreview({ emailConfig }: { emailConfig: EmailConfig }) {
  const theme = useTheme();
  return (
      <Paper
        data-testid="signature-preview"
        sx={{
          p: 0,
          backgroundColor: "#ffffff",
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        {/* Email Preview Header */}
        <Box
          sx={{
            p: 2,
            backgroundColor: alpha(theme.palette.grey[100], 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Preview of your email signature
          </Typography>
        </Box>

        {/* Signature Content */}
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 3,
              borderTop: `3px solid ${theme.palette.primary.main}`,
              pt: 2,
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={COMPANY_LOGO_URL}
                alt="Company Logo"
                style={{
                  maxWidth: 100,
                  maxHeight: 100,
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </Box>

            {/* Divider */}
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            {/* Contact Info */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 0.5,
                  fontSize: "1.1rem",
                }}
              >
                {emailConfig.agentName || "Your Name"}
              </Typography>

              {emailConfig.jobTitle && (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    mb: 1,
                  }}
                >
                  {emailConfig.jobTitle}
                </Typography>
              )}

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 1.5,
                }}
              >
                {COMPANY_NAME}
              </Typography>

              <Stack spacing={0.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {emailConfig.agentPhone || "(XXX) XXX-XXXX"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {emailConfig.agentEmail || "email@example.com"}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          {/* Footer text */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              // NOT text.disabled (#94A3B8 = 2.56:1 on white). Freezing the
              // palette made this line DETERMINISTIC, not legible — a reviewer
              // caught that my anchor set happened to exclude the worst
              // remaining line on the very surface it gates.
              color: theme.palette.text.secondary,
              textAlign: "center",
            }}
          >
            Powered by LocateNow
          </Typography>
        </Box>
      </Paper>
  );
}

const EmailSettingsTab: React.FC<EmailSettingsTabProps> = ({
  emailConfig,
  setEmailConfig,
  validationErrors,
  setValidationErrors,
  onSave,
}) => {
  const theme = useTheme();
  // Frozen light palette for the signature preview — see the comment at the
  // preview surface. Built once; it never depends on the app's mode.
  const previewTheme = React.useMemo(() => buildTheme("light"), []);

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setEmailConfig((prev) => ({ ...prev, agentPhone: formatted }));

    if (formatted && !validatePhone(formatted)) {
      setValidationErrors((prev) => ({
        ...prev,
        agentPhone: "Please enter a valid phone number",
      }));
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.agentPhone;
        return newErrors;
      });
    }
  };

  const handleEmailChange = (value: string) => {
    setEmailConfig((prev) => ({ ...prev, agentEmail: value }));

    if (value && !validateEmail(value)) {
      setValidationErrors((prev) => ({
        ...prev,
        agentEmail: "Please enter a valid email address",
      }));
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.agentEmail;
        return newErrors;
      });
    }
  };

  return (
    <>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Branding & Signature
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This information appears on guest cards and listing emails you send.
          Fields are pre-populated from your profile.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Your Name (shown on emails)"
              value={emailConfig.agentName}
              onChange={(e) =>
                setEmailConfig({
                  ...emailConfig,
                  agentName: e.target.value,
                })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={emailConfig.jobTitle}
              onChange={(e) =>
                setEmailConfig({
                  ...emailConfig,
                  jobTitle: e.target.value,
                })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email Address"
              value={emailConfig.agentEmail}
              onChange={(e) => handleEmailChange(e.target.value)}
              error={!!validationErrors.agentEmail}
              helperText={validationErrors.agentEmail}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={emailConfig.agentPhone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              error={!!validationErrors.agentPhone}
              helperText={
                validationErrors.agentPhone || "Format: (XXX) XXX-XXXX"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
              inputProps={{ maxLength: 14 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company Name"
              value={COMPANY_NAME}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={
              !!validationErrors.agentEmail || !!validationErrors.agentPhone
            }
          >
            Save Email Settings
          </Button>
        </Box>
      </Card>

      {/* Professional Email Signature Preview */}
      <Card sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Signature Preview
        </Typography>
        {/* The white background is DELIBERATE: this previews what the
            RECIPIENT sees, and email clients render on white. What was wrong
            was pairing that fixed surface with theme tokens that flip — in
            dark mode `text.primary` (#F1F5F9) on #ffffff measured 1.10:1 and
            the signature was invisible, the same class as the SMS bubble bug.
            The surface stays frozen light and so does its FOREGROUND: the
            nested ThemeProvider resolves string tokens, and previewTheme
            resolves the explicit ones. Any child added later inherits the fix
            instead of re-introducing the bug. */}
        <ThemeProvider theme={previewTheme}>
          <SignaturePreview emailConfig={emailConfig} />
        </ThemeProvider>
      </Card>
    </>
  );
};

export default EmailSettingsTab;
